/**
 * Royal Tulip Kosher Hotel 5* — Tbilisi allotment rates (USD, per room / night).
 * Season: 01/09/2026–01/09/2027 except NY 25/12/2026–06/01/2027.
 * Friday–Saturday nights use the Shabbat meal package (Friday dinner, Saturday
 * breakfast, Seudah 1 & Seudah 2).
 */
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

export const ROYAL_TULIP_CAMPAIGN_SLUG = "royal-tulip-tbilisi-2026";
export const ROYAL_TULIP_SEASON_START = "2026-09-01";
export const ROYAL_TULIP_SEASON_END = "2027-09-01";
export const ROYAL_TULIP_NY_FROM = "2026-12-25";
export const ROYAL_TULIP_NY_TO = "2027-01-06";

function parseIso(value) {
  return dayjs(value, "YYYY-MM-DD", true);
}

export function isRoyalTulipNyDate(iso) {
  const d = String(iso || "").slice(0, 10);
  return d >= ROYAL_TULIP_NY_FROM && d <= ROYAL_TULIP_NY_TO;
}

export function isRoyalTulipShabbatNight(iso) {
  const d = parseIso(iso);
  if (!d.isValid()) return false;
  const weekday = d.day();
  return weekday === 5 || weekday === 6;
}

export function resolveRoyalTulipSeasonForDate(iso) {
  const d = parseIso(iso);
  if (!d.isValid()) return null;
  const start = parseIso(ROYAL_TULIP_SEASON_START);
  const end = parseIso(ROYAL_TULIP_SEASON_END);
  if (d.isBefore(start, "day") || d.isAfter(end, "day")) return null;
  if (isRoyalTulipNyDate(iso)) return null;
  return isRoyalTulipShabbatNight(iso) ? "SHABBAT" : "REGULAR";
}

function occupancyRoom({
  index,
  id,
  name,
  nameHe,
  minAdults,
  maxAdults,
  maxChildren,
  minOccupancy,
  maxOccupancy,
  occupancyLabel,
  imageKey,
  regularBb,
  regularHb,
  shabbatBb,
  shabbatHb,
  infantsAllowed = true,
}) {
  const occupancy = {
    minAdults,
    maxAdults,
    maxChildren,
    minOccupancy,
    maxOccupancy,
    occupancyLabel,
    imageKey,
    chargeType: "per_unit",
    allotment: 99,
    infantsAllowed,
  };
  return [
    {
      ...occupancy,
      code: index * 2 - 1,
      id: `${id}-bb`,
      name: `${name} BB`,
      nameHe: `${nameHe} · לינה וארוחת בוקר`,
      board: "BB",
      boardHe: "ארוחת בוקר כשרה",
      seasonalPrices: { REGULAR: regularBb, SHABBAT: shabbatBb },
    },
    {
      ...occupancy,
      code: index * 2,
      id: `${id}-hb`,
      name: `${name} HB`,
      nameHe: `${nameHe} · חצי פנסיון כשר`,
      board: "HB",
      boardHe: "חצי פנסיון כשר",
      seasonalPrices: { REGULAR: regularHb, SHABBAT: shabbatHb },
    },
  ];
}

export const royalTulipTbilisiContract = {
  hotelCode: ROYAL_TULIP_CAMPAIGN_SLUG,
  hotelName: "Royal Tulip Kosher Hotel",
  hotelNameHe: "Royal Tulip Kosher Hotel",
  location: "Tbilisi, Georgia",
  locationHe: "טביליסי, גאורגיה",
  starRating: 5,
  kosher: true,
  trustBeachBadgeHe: "בריכה · כשר למהדרין",
  board: "BB",
  boardHe: "ארוחת בוקר כשרה כלולה",
  seasonLabel: "2026 / 2027",
  currency: "USD",
  publicListMarkupPercent: 30,
  publicEffectiveMarkupTargetPercent: 20,
  publicSaleDiscountPercent: 10,
  agentMarkupPercent: 10,
  defaultMarkupPercent: 25,
  minStayNights: 1,
  releaseNights: 1,
  accommodationTaxPerRoomPerNight: 0,
  outOfSeasonMessage:
    "חלק מתאריכי השהייה מחוץ לעונת החוזה, או כוללים את תקופת ראש השנה האזרחית (25/12/2026–06/01/2027) שאינה זמינה במחיר זה.",
  resolveSeasonForDate: resolveRoyalTulipSeasonForDate,
  seasons: [
    {
      code: "REGULAR",
      from: ROYAL_TULIP_SEASON_START,
      to: ROYAL_TULIP_SEASON_END,
      minStayNights: 1,
      releaseNights: 1,
    },
    {
      code: "SHABBAT",
      from: ROYAL_TULIP_SEASON_START,
      to: ROYAL_TULIP_SEASON_END,
      minStayNights: 1,
      releaseNights: 1,
    },
  ],
  roomTypes: [
    ...occupancyRoom({
      index: 1,
      id: "standard-single",
      name: "Standard Single Room",
      nameHe: "חדר סטנדרט יחיד",
      minAdults: 1,
      maxAdults: 1,
      maxChildren: 0,
      minOccupancy: 1,
      maxOccupancy: 1,
      occupancyLabel: "מבוגר יחיד",
      imageKey: "standard",
      regularBb: 126,
      regularHb: 166,
      shabbatBb: 196,
      shabbatHb: 266,
    }),
    ...occupancyRoom({
      index: 2,
      id: "standard-double",
      name: "Standard Double/Twin Room",
      nameHe: "חדר סטנדרט זוגי / Twin",
      minAdults: 2,
      maxAdults: 2,
      maxChildren: 0,
      minOccupancy: 2,
      maxOccupancy: 2,
      occupancyLabel: "2 מבוגרים",
      imageKey: "standard",
      regularBb: 136,
      regularHb: 216,
      shabbatBb: 276,
      shabbatHb: 416,
    }),
    ...occupancyRoom({
      index: 3,
      id: "standard-triple-3adl",
      name: "Standard Double/Twin + Extra Bed (Triple) 3 ADL",
      nameHe: "חדר טריפל · 3 מבוגרים",
      minAdults: 3,
      maxAdults: 3,
      maxChildren: 0,
      minOccupancy: 3,
      maxOccupancy: 3,
      occupancyLabel: "3 מבוגרים",
      imageKey: "standard",
      regularBb: 196,
      regularHb: 316,
      shabbatBb: 406,
      shabbatHb: 616,
    }),
    ...occupancyRoom({
      index: 4,
      id: "standard-triple-2adl-1chd",
      name: "Standard Double/Twin + Extra Bed (Triple) 2 ADL + 1 child",
      nameHe: "חדר טריפל · 2 מבוגרים + ילד",
      minAdults: 2,
      maxAdults: 2,
      maxChildren: 1,
      minOccupancy: 3,
      maxOccupancy: 3,
      occupancyLabel: "2 מבוגרים + 1 ילד (3–11.99)",
      imageKey: "standard",
      regularBb: 166,
      regularHb: 266,
      shabbatBb: 346,
      shabbatHb: 526,
    }),
    ...occupancyRoom({
      index: 5,
      id: "family-4adl",
      name: "Family Connecting Rooms for 4 Adults",
      nameHe: "חדרי משפחה מחוברים · 4 מבוגרים",
      minAdults: 4,
      maxAdults: 4,
      maxChildren: 0,
      minOccupancy: 4,
      maxOccupancy: 4,
      occupancyLabel: "4 מבוגרים",
      imageKey: "family",
      regularBb: 272,
      regularHb: 432,
      shabbatBb: 552,
      shabbatHb: 832,
    }),
    ...occupancyRoom({
      index: 6,
      id: "family-3adl-1chd",
      name: "Family Connecting Rooms for 3 Adults + 1 Child",
      nameHe: "חדרי משפחה מחוברים · 3 מבוגרים + ילד",
      minAdults: 3,
      maxAdults: 3,
      maxChildren: 1,
      minOccupancy: 4,
      maxOccupancy: 4,
      occupancyLabel: "3 מבוגרים + 1 ילד (3–11.99)",
      imageKey: "family",
      regularBb: 267,
      regularHb: 412,
      shabbatBb: 517,
      shabbatHb: 767,
    }),
    ...occupancyRoom({
      index: 7,
      id: "family-2adl-2chd",
      name: "Family Connecting Rooms for 2 Adults + 2 Children",
      nameHe: "חדרי משפחה מחוברים · 2 מבוגרים + 2 ילדים",
      minAdults: 2,
      maxAdults: 2,
      maxChildren: 2,
      minOccupancy: 4,
      maxOccupancy: 4,
      occupancyLabel: "2 מבוגרים + 2 ילדים (3–11.99)",
      imageKey: "family",
      regularBb: 262,
      regularHb: 392,
      shabbatBb: 482,
      shabbatHb: 702,
    }),
    ...occupancyRoom({
      index: 8,
      id: "family-1adl-3chd",
      name: "Family Connecting Rooms for 1 Adult + 3 Children",
      nameHe: "חדרי משפחה מחוברים · מבוגר + 3 ילדים",
      minAdults: 1,
      maxAdults: 1,
      maxChildren: 3,
      minOccupancy: 4,
      maxOccupancy: 4,
      occupancyLabel: "מבוגר 1 + 3 ילדים (3–11.99)",
      imageKey: "family",
      regularBb: 257,
      regularHb: 372,
      shabbatBb: 447,
      shabbatHb: 637,
    }),
    ...occupancyRoom({
      index: 9,
      id: "family-4adl-1chd",
      name: "Family Connecting Rooms for 4 Adults + 1 Child",
      nameHe: "חדרי משפחה מחוברים · 4 מבוגרים + ילד",
      minAdults: 4,
      maxAdults: 4,
      maxChildren: 1,
      minOccupancy: 5,
      maxOccupancy: 5,
      occupancyLabel: "4 מבוגרים + 1 ילד (3–11.99)",
      imageKey: "family",
      regularBb: 302,
      regularHb: 482,
      shabbatBb: 622,
      shabbatHb: 942,
    }),
    ...occupancyRoom({
      index: 10,
      id: "family-3adl-2chd",
      name: "Family Connecting Rooms for 3 Adults + 2 Children",
      nameHe: "חדרי משפחה מחוברים · 3 מבוגרים + 2 ילדים",
      minAdults: 3,
      maxAdults: 3,
      maxChildren: 2,
      minOccupancy: 5,
      maxOccupancy: 5,
      occupancyLabel: "3 מבוגרים + 2 ילדים (3–11.99)",
      imageKey: "family",
      regularBb: 297,
      regularHb: 457,
      shabbatBb: 587,
      shabbatHb: 877,
    }),
    ...occupancyRoom({
      index: 11,
      id: "family-2adl-3chd",
      name: "Family Connecting Rooms for 2 Adults + 3 Children",
      nameHe: "חדרי משפחה מחוברים · 2 מבוגרים + 3 ילדים",
      minAdults: 2,
      maxAdults: 2,
      maxChildren: 3,
      minOccupancy: 5,
      maxOccupancy: 5,
      occupancyLabel: "2 מבוגרים + 3 ילדים (3–11.99)",
      imageKey: "family",
      regularBb: 292,
      regularHb: 432,
      shabbatBb: 552,
      shabbatHb: 812,
    }),
    ...occupancyRoom({
      index: 12,
      id: "family-1adl-4chd",
      name: "Family Connecting Rooms for 1 Adult + 4 Children",
      nameHe: "חדרי משפחה מחוברים · מבוגר + 4 ילדים",
      minAdults: 1,
      maxAdults: 1,
      maxChildren: 4,
      minOccupancy: 5,
      maxOccupancy: 5,
      occupancyLabel: "מבוגר 1 + 4 ילדים (3–11.99)",
      imageKey: "family",
      regularBb: 287,
      regularHb: 407,
      shabbatBb: 517,
      shabbatHb: 747,
    }),
    ...occupancyRoom({
      index: 13,
      id: "family-4adl-2chd",
      name: "Family Connecting Rooms for 4 Adults + 2 Children",
      nameHe: "חדרי משפחה מחוברים · 4 מבוגרים + 2 ילדים",
      minAdults: 4,
      maxAdults: 4,
      maxChildren: 2,
      minOccupancy: 6,
      maxOccupancy: 6,
      occupancyLabel: "4 מבוגרים + 2 ילדים (3–11.99)",
      imageKey: "family",
      regularBb: 332,
      regularHb: 532,
      shabbatBb: 692,
      shabbatHb: 1052,
    }),
    ...occupancyRoom({
      index: 14,
      id: "family-3adl-3chd",
      name: "Family Connecting Rooms for 3 Adults + 3 Children",
      nameHe: "חדרי משפחה מחוברים · 3 מבוגרים + 3 ילדים",
      minAdults: 3,
      maxAdults: 3,
      maxChildren: 3,
      minOccupancy: 6,
      maxOccupancy: 6,
      occupancyLabel: "3 מבוגרים + 3 ילדים (3–11.99)",
      imageKey: "family",
      regularBb: 327,
      regularHb: 512,
      shabbatBb: 657,
      shabbatHb: 987,
    }),
    ...occupancyRoom({
      index: 15,
      id: "family-2adl-4chd",
      name: "Family Connecting Rooms for 2 Adults + 4 Children",
      nameHe: "חדרי משפחה מחוברים · 2 מבוגרים + 4 ילדים",
      minAdults: 2,
      maxAdults: 2,
      maxChildren: 4,
      minOccupancy: 6,
      maxOccupancy: 6,
      occupancyLabel: "2 מבוגרים + 4 ילדים (3–11.99)",
      imageKey: "family",
      regularBb: 322,
      regularHb: 492,
      shabbatBb: 622,
      shabbatHb: 922,
    }),
    ...occupancyRoom({
      index: 16,
      id: "family-1adl-5chd",
      name: "Family Connecting Rooms for 1 Adult + 5 Children",
      nameHe: "חדרי משפחה מחוברים · מבוגר + 5 ילדים",
      minAdults: 1,
      maxAdults: 1,
      maxChildren: 5,
      minOccupancy: 6,
      maxOccupancy: 6,
      occupancyLabel: "מבוגר 1 + 5 ילדים (3–11.99)",
      imageKey: "family",
      regularBb: 317,
      regularHb: 472,
      shabbatBb: 587,
      shabbatHb: 857,
    }),
  ],
  extras: {
    extraBedAdultBbUsd: 60,
    extraBedChildBbUsd: 30,
    extraKosherDinnerAdultUsd: 40,
    extraKosherDinnerChildUsd: 20,
    shabbatDinnerAdultUsd: 70,
    shabbatDinnerChildUsd: 40,
    shabbatBreakfastAdultUsd: 70,
    shabbatBreakfastChildUsd: 40,
    kosherLunchBoxUsd: 10,
  },
  remarks: [
    "ארוחת בוקר כשרה כלולה.",
    "ילדים מחויבים מגיל 3 עד 11.99. מתחת לגיל 3 — עריסה לתינוק ללא תשלום.",
    "לילות שישי–שבת מתומחרים לפי חבילת שבת: ארוחת ערב שישי, ארוחת בוקר שבת, סעודה 1 וסעודה 2.",
    "תקופת ראש השנה האזרחית 25/12/2026–06/01/2027 אינה כלולה במחירים אלה.",
    "מיטה נוספת: 60$ BB למבוגר, 30$ BB לילד. כל חדר יכול להכיל מיטה נוספת אחת.",
    "ארוחת ערב כשרה נוספת: 40$ למבוגר, 20$ לילד.",
    "ארוחת ערב שבת (שישי): 70$ למבוגר, 40$ לילד. ארוחת בוקר שבת: 70$ למבוגר, 40$ לילד.",
    "קופסת אוכל כשרה: 10$ לאדם.",
    "תפוסה מקסימלית בחדרי משפחה מחוברים: 6 אורחים. ל-6 מבוגרים יש להזמין 2 חדרי טריפל.",
    "גישה חינם לבריכה 08:00–10:00: 08:00–09:00 נשים, 09:00–10:00 גברים.",
    "3 מבוגרים + ילד מתחת לגיל 4: מומלץ חדר טריפל עם עריסה חינם.",
  ],
  cancellationPolicy: ["המחיר כפוף לאישור זמינות סופי מול המלון"],
  paymentPolicy: "המחיר כפוף לאישור זמינות סופי מול המלון",
  disclaimerHe: "המחיר כפוף לאישור זמינות סופי מול המלון",
};

export const ROYAL_TULIP_FREE_CANCELLATION_BADGE_HE = null;
